import { useEffect, useRef, useState, type ImgHTMLAttributes } from "react";

/** Foto del redimensionador que aguanta un fallo pasajero (5-11-2026). Con
 *  muchas fotos sin cachear, Supabase responde 429 y el `onError` que caía al
 *  original se llevaba otro 429: icono de imagen rota con la foto sana.
 *  Espera y repite; luego prueba el original, sin `srcSet`, que si no seguiría
 *  mandando; si tampoco, no pinta nada y se queda el fondo de la caja. */
const RETRY_DELAYS_MS = [2000, 5000];

function originalUrl(src: string): string {
  if (/\.(320|640|1280)\.webp$/.test(src)) return src.replace(/\.(320|640|1280)\.webp$/, "");
  if (!src.includes("/storage/v1/render/image/public/")) return src;
  return src.replace("/storage/v1/render/image/public/", "/storage/v1/object/public/").split("?")[0];
}

export function RetryImg({ src, srcSet, sizes, alt = "", ...rest }: ImgHTMLAttributes<HTMLImageElement> & { src: string }) {
  const [attempt, setAttempt] = useState(0);
  const timer = useRef<number>();

  useEffect(() => {
    setAttempt(0);
    return () => window.clearTimeout(timer.current);
  }, [src]);

  const retries = RETRY_DELAYS_MS.length;
  const original = originalUrl(src);
  if (attempt > retries + (original === src ? 0 : 1)) return null;
  const fallingBack = attempt > retries;

  return (
    <img
      // La clave vuelve a montar la etiqueta: repetir el mismo src no pide nada.
      key={attempt}
      {...rest}
      src={fallingBack ? original : src}
      srcSet={fallingBack ? undefined : srcSet}
      sizes={fallingBack ? undefined : sizes}
      alt={alt}
      onError={() => {
        // Con un poco de azar, para que no vuelvan todas a la vez.
        const wait = attempt < retries ? RETRY_DELAYS_MS[attempt] * (1 + Math.random()) : 0;
        timer.current = window.setTimeout(() => setAttempt(attempt + 1), wait);
      }}
    />
  );
}
